import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import Container from "@components/Container";
import RichText from "@components/RichText";

import { useStore } from "@Store/";

import {
  OurMissionGridMain,
  OurMissionGridWrapper,
  OurMissionGridHeader,
  OurMissionGridTitle,
  OurMissionGridCenterLine,
  OurMissionGridItems,
  OurMissionGridItem,
  OurMissionGridItemImage,
  OurMissionGridItemOverlay,
  OurMissionGridItemText,
  OurMissionGridFooter,
  OurMissionGridSubTitle,
} from "./index.style";

gsap.registerPlugin(ScrollTrigger);

const getAssetUrl = (asset) => {
  const url = asset?.file?.url || asset?.url;

  if (!url) return null;

  return url.startsWith("//") ? `https:${url}` : url;
};

const getAssetAlt = (asset, fallback = "") =>
  asset?.description || asset?.title || fallback;

const OurMissionGrid = ({
  id,
  title,
  subTitle,
  items,
}) => {
  const [store] = useStore();

  const sectionRef = useRef(null);
  const lineRef = useRef(null);
  const titleRef = useRef(null);
  const subTitleRef = useRef(null);
  const itemsRef = useRef([]);
  const smoothScrollRef = useRef(store?.smoothScroll);

  const sectionId = id || "our-mission-grid";

  smoothScrollRef.current = store?.smoothScroll;

  const addItemRef = (element, index) => {
    if (!element) return;

    itemsRef.current[index] = element;
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const section = sectionRef.current;
    const line = lineRef.current;
    const titleElement = titleRef.current;
    const subTitleElement = subTitleRef.current;
    const gridItems = itemsRef.current.filter(Boolean);

    if (!section) return undefined;

    let refreshFrameId;

    const refresh = () => {
      if (
        smoothScrollRef.current &&
        typeof smoothScrollRef.current.refresh === "function"
      ) {
        smoothScrollRef.current.refresh();
      } else {
        ScrollTrigger.refresh();
      }
    };

    const refreshAfterLayout = () => {
      refreshFrameId = requestAnimationFrame(() => {
        refreshFrameId = requestAnimationFrame(refresh);
      });
    };

    const handleResize = () => {
      gsap.matchMediaRefresh();
      refreshAfterLayout();
    };

    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      mm.add("(min-width:1024px)", () => {
        const animationTargets = [
          line,
          titleElement,
          subTitleElement,
          ...gridItems,
        ].filter(Boolean);

        if (!animationTargets.length) return undefined;

        if (line) {
          gsap.set(line, {
            scaleY: 0,
            transformOrigin: "top center",
          });
        }

        if (titleElement) {
          gsap.set(titleElement, {
            autoAlpha: 0,
            x: 80,
          });
        }

        if (subTitleElement) {
          gsap.set(subTitleElement, {
            autoAlpha: 0,
            x: -80,
          });
        }

        if (gridItems.length) {
          gsap.set(gridItems, {
            autoAlpha: 0,
            y: 80,
          });
        }

        const timeline = gsap.timeline({
          scrollTrigger: {
            trigger: section,
            start: "top 75%",
            toggleActions: "play none none none",
            once: true,
            invalidateOnRefresh: true,
          },
        });

        if (line) {
          timeline.to(line, {
            scaleY: 1,
            duration: 1.2,
            ease: "power2.inOut",
          });
        }

        if (titleElement) {
          timeline.to(
            titleElement,
            {
              autoAlpha: 1,
              x: 0,
              duration: 0.9,
              ease: "power3.out",
              clearProps: "transform,opacity,visibility",
            },
            0.15,
          );
        }

        if (subTitleElement) {
          timeline.to(
            subTitleElement,
            {
              autoAlpha: 1,
              x: 0,
              duration: 0.9,
              ease: "power3.out",
              clearProps: "transform,opacity,visibility",
            },
            0.25,
          );
        }

        if (gridItems.length) {
          timeline.to(
            gridItems,
            {
              autoAlpha: 1,
              y: 0,
              duration: 0.8,
              stagger: 0.15,
              ease: "power3.out",
              clearProps:
                "transform,opacity,visibility,willChange",
            },
            0.45,
          );
        }

        refreshAfterLayout();

        return () => {
          timeline.scrollTrigger?.kill();
          timeline.kill();
        };
      });

      mm.add("(max-width:1023px)", () => {
        const mobileElements = [
          line,
          titleElement,
          subTitleElement,
          ...gridItems,
        ].filter(Boolean);

        gsap.set(mobileElements, {
          clearProps: "all",
        });
      });

      refreshAfterLayout();
    }, section);

    window.addEventListener("load", refreshAfterLayout);
    window.addEventListener("resize", handleResize);

    return () => {
      if (refreshFrameId) {
        cancelAnimationFrame(refreshFrameId);
      }

      ctx.revert();

      window.removeEventListener(
        "load",
        refreshAfterLayout,
      );
      window.removeEventListener("resize", handleResize);

      itemsRef.current = [];
    };
  }, [items, store?.smoothScroll]);

  return (
    <OurMissionGridMain
      id={sectionId}
      ref={sectionRef}
    >
      <Container>
        <OurMissionGridWrapper>
          <OurMissionGridHeader>
            <OurMissionGridCenterLine
              ref={lineRef}
              aria-hidden="true"
            />

            {title && (
              <OurMissionGridTitle ref={titleRef}>
                <RichText content={title} />
              </OurMissionGridTitle>
            )}
          </OurMissionGridHeader>

          {!!items.length && (
            <OurMissionGridItems>
              {items.map((item, index) => {
                const imageUrl = getAssetUrl(item?.image);
                const imageAlt = getAssetAlt(item?.image);

                return (
                  <OurMissionGridItem
                    key={
                      item?.id ||
                      item?.contentful_id ||
                      index
                    }
                    ref={(element) =>
                      addItemRef(element, index)
                    }
                  >
                    {imageUrl && (
                      <OurMissionGridItemImage
                        src={imageUrl}
                        alt={imageAlt}
                        loading="lazy"
                      />
                    )}

                    <OurMissionGridItemOverlay
                      aria-hidden="true"
                      className="overlay"
                    />

                    {item?.text && (
                      <OurMissionGridItemText className="text">
                        <RichText content={item.text} />
                      </OurMissionGridItemText>
                    )}
                  </OurMissionGridItem>
                );
              })}
            </OurMissionGridItems>
          )}

          {subTitle && (
            <OurMissionGridFooter>
              <OurMissionGridSubTitle ref={subTitleRef}>
                <RichText content={subTitle} />
              </OurMissionGridSubTitle>
            </OurMissionGridFooter>
          )}
        </OurMissionGridWrapper>
      </Container>
    </OurMissionGridMain>
  );
};

OurMissionGrid.propTypes = {
  id: PropTypes.string,
  title: PropTypes.object,
  subTitle: PropTypes.object,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      contentful_id: PropTypes.string,
      text: PropTypes.object,
      image: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        url: PropTypes.string,
        file: PropTypes.shape({
          url: PropTypes.string,
        }),
      }),
    }),
  ),
};

OurMissionGrid.defaultProps = {
  id: "our-mission-grid",
  title: null,
  subTitle: null,
  items: [],
};

export default OurMissionGrid;